import React, { useEffect, useState } from 'react';
import { Paperclip, Plus, X } from 'lucide-react';
import { toast } from 'react-toastify';

import {
  Attachment,
  AttachmentAction,
  AttachmentActions,
  AttachmentContent,
  AttachmentGroup,
  AttachmentMedia,
  AttachmentTitle,
} from '@/components/ui/attachment';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import ChatFileSelector from 'components/DisasterGPT/ChatFileSelector';
import {
  useAttachedFiles,
  useAttachFiles,
  useDetachFile,
} from 'components/DisasterGPT/hooks/useSitrepsV2';
import StylishViewFileDialog from 'components/DesignSystems/New/StylishViewFileDialog';
import { useSignedUrl } from 'components/FileManagement/hooks/useNotebookQueries';
import { Spinner } from '@/components/ui/spinner';

interface ReportsFileAttachmentsProps {
  sitrepId: string;
  workspaceId: string | null;
  readOnly?: boolean;
  pickerOpen?: boolean;
  onPickerOpenChange?: (open: boolean) => void;
  hideAddButton?: boolean;
}

const ReportsFileAttachments: React.FC<ReportsFileAttachmentsProps> = ({
  sitrepId,
  workspaceId,
  readOnly = false,
  pickerOpen,
  onPickerOpenChange,
  hideAddButton = false,
}) => {
  const [uncontrolledPickerOpen, setUncontrolledPickerOpen] = useState(false);
  const isPickerControlled = pickerOpen !== undefined;
  const isExpanded = isPickerControlled ? pickerOpen : uncontrolledPickerOpen;
  const setIsExpanded = (open: boolean) => {
    if (isPickerControlled) {
      onPickerOpenChange?.(open);
      return;
    }
    setUncontrolledPickerOpen(open);
  };
  const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);
  const [showFileDialog, setShowFileDialog] = useState(false);
  const [fileViewUrl, setFileViewUrl] = useState<string | null>(null);
  const [currentFileName, setCurrentFileName] = useState<string>('');

  const { data: attachedFiles = [], isLoading: loadingAttached } =
    useAttachedFiles(sitrepId);

  const attachMutation = useAttachFiles();
  const detachMutation = useDetachFile();
  const { mutateAsync: getSignedUrl } = useSignedUrl();

  useEffect(() => {
    if (isExpanded) {
      setSelectedFileIds(attachedFiles.map((f) => f.id));
    } else {
      setSelectedFileIds([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- attachedFiles is snapshotted on open
  }, [isExpanded]);

  const handleAddFiles = async () => {
    if (selectedFileIds.length === 0) return;

    const alreadyAttachedIds = attachedFiles.map((f) => f.id);
    const newFileIds = selectedFileIds.filter(
      (id) => !alreadyAttachedIds.includes(id)
    );

    if (newFileIds.length === 0) {
      setIsExpanded(false);
      return;
    }

    await attachMutation.mutateAsync({
      sitrepId,
      fileIds: newFileIds,
    });

    setSelectedFileIds([]);
    setIsExpanded(false);
  };

  const handleDetachFile = async (fileId: string) => {
    await detachMutation.mutateAsync({
      sitrepId,
      fileId,
    });
  };

  const handleViewFile = async (fileUrl: string, fileName: string) => {
    try {
      const url = await getSignedUrl(fileUrl);
      setFileViewUrl(url);
      setCurrentFileName(fileName);
      setShowFileDialog(true);
    } catch (error) {
      console.error('Error viewing file:', error);
      toast.error('Failed to view file');
    }
  };

  const isDisabled = !workspaceId;

  const openPicker = () => {
    setSelectedFileIds(attachedFiles.map((f) => f.id));
    setIsExpanded(true);
  };

  const addFilesButton = hideAddButton ? null : (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={openPicker}
      disabled={isDisabled}
    >
      <Plus />
      Add Files
    </Button>
  );

  return (
    <div className="reports-file-attachments">
      {workspaceId && loadingAttached ? (
        <Spinner />
      ) : attachedFiles.length > 0 ? (
        <div className="tw:mb-3">
          <div className="tw:mb-2 tw:flex tw:items-center tw:gap-2 tw:text-sm tw:font-medium">
            <Paperclip className="tw:size-4" />
            Attached Files ({attachedFiles.length})
          </div>
          <AttachmentGroup>
            {attachedFiles.map((file) => (
              <Attachment key={file.id} size="sm">
                <AttachmentMedia>
                  <Paperclip />
                </AttachmentMedia>
                <AttachmentContent>
                  <AttachmentTitle>{file.file_name}</AttachmentTitle>
                </AttachmentContent>
                <AttachmentActions>
                  {readOnly ? null : (
                    <AttachmentAction
                      aria-label={`Detach ${file.file_name}`}
                      onClick={() => {
                        void handleDetachFile(file.id);
                      }}
                    >
                      <X />
                    </AttachmentAction>
                  )}
                </AttachmentActions>
                <button
                  type="button"
                  className="tw:absolute tw:inset-0 tw:z-10 tw:cursor-pointer tw:bg-transparent tw:p-0"
                  aria-label={`View ${file.file_name}`}
                  onClick={() => {
                    void handleViewFile(file.fileUrl, file.file_name);
                  }}
                />
              </Attachment>
            ))}
          </AttachmentGroup>
        </div>
      ) : null}

      {!readOnly &&
        (!isExpanded ? (
          addFilesButton ? (
            isDisabled ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger render={<span>{addFilesButton}</span>} />
                  <TooltipContent className="dark">
                    Link this report to an incident to attach files
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              addFilesButton
            )
          ) : null
        ) : (
          <div className="tw:mt-2">
            <div className="tw:rounded-md tw:border tw:border-border">
              <ChatFileSelector
                workspaceId={workspaceId!}
                selectedFileIds={selectedFileIds}
                onFileSelectionChange={setSelectedFileIds}
                selectionText="selected"
              />
            </div>
            <div className="tw:mt-2 tw:flex tw:justify-end tw:gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsExpanded(false);
                  setSelectedFileIds([]);
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  void handleAddFiles();
                }}
                disabled={
                  selectedFileIds.length === 0 || attachMutation.isPending
                }
              >
                {attachMutation.isPending ? 'Attaching...' : 'Attach Files'}
              </Button>
            </div>
          </div>
        ))}

      {showFileDialog && fileViewUrl && (
        <StylishViewFileDialog
          show={showFileDialog}
          onClose={() => setShowFileDialog(false)}
          fileUrl={fileViewUrl}
          fileName={currentFileName}
        />
      )}
    </div>
  );
};

export default ReportsFileAttachments;
